import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { AdminButton, AdminCard, AdminField, EmptyState, adminInput } from "@/components/admin/ui";

const TABS = [
  ["pending", "Da leggere"],
  ["approved", "Pubblicate"],
  ["rejected", "Archiviate"],
];

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminIdeas() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/diario/idee?status=${tab}`);
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const save = async (idea, status) => {
    setSaving(true);
    try {
      await api.put(`/admin/diario/idee/${idea.id}`, {
        title: idea.title,
        body: idea.body,
        display_name: idea.display_name,
        editor_note: idea.editor_note || "",
        status,
      });
      toast.success(status === "approved" ? "Idea pubblicata" : "Idea aggiornata");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/diario/idee/${id}`);
      toast.success("Contributo eliminato");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-ideas">
      <div>
        <p className="eyebrow text-sage">Diario dal Borgo</p>
        <h1 className="mt-2 font-serif text-3xl text-wood">Le idee di chi legge</h1>
        <p className="mt-2 text-sm text-foreground/65">
          Nessun contributo diventa pubblico senza la tua approvazione.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`ideas-tab-${key}`}
            className={`px-4 py-2 text-[0.66rem] uppercase tracking-[0.16em] border transition-colors duration-300 ${
              tab === key ? "bg-sage text-cream border-sage" : "border-wood/25 text-wood/70 hover:border-wood"
            }`}
          >
            {label} {data.counts?.[key] ? `(${data.counts[key]})` : ""}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-wood/60"><Loader2 className="inline animate-spin" size={14} /> Carico…</p>}

      {!loading && data.items.length === 0 && (
        <EmptyState title="Nessun contributo" description="Qui arrivano le idee inviate dai lettori." testId="ideas-empty" />
      )}

      {data.items.map((idea) => {
        const draft = editing?.id === idea.id ? editing : idea;
        return (
          <AdminCard key={idea.id} title={draft.title} testId={`idea-${idea.id}`}>
            <p className="text-xs text-foreground/50">
              {fmt(idea.created_at)} · firma: {idea.display_name}
              {idea.email ? ` · ${idea.email} (mai pubblica)` : " · nessuna email"}
            </p>

            {editing?.id === idea.id ? (
              <div className="mt-4 space-y-4">
                <AdminField label="Titolo">
                  <input className={adminInput} value={draft.title} onChange={(e) => setEditing({ ...draft, title: e.target.value })} data-testid="idea-edit-title" />
                </AdminField>
                <AdminField label="Testo">
                  <textarea className={`${adminInput} h-40`} value={draft.body} onChange={(e) => setEditing({ ...draft, body: e.target.value })} data-testid="idea-edit-body" />
                </AdminField>
                <AdminField label="Firma mostrata">
                  <input className={adminInput} value={draft.display_name} onChange={(e) => setEditing({ ...draft, display_name: e.target.value })} data-testid="idea-edit-name" />
                </AdminField>
                <AdminField label="Nota della redazione (facoltativa)">
                  <textarea className={`${adminInput} h-24`} value={draft.editor_note || ""} onChange={(e) => setEditing({ ...draft, editor_note: e.target.value })} data-testid="idea-edit-note" />
                </AdminField>
                <div className="flex flex-wrap gap-2">
                  <AdminButton onClick={() => save(draft, "approved")} disabled={saving} data-testid="idea-approve">
                    <Check size={14} className="inline" /> Pubblica
                  </AdminButton>
                  <AdminButton variant="ghost" onClick={() => save(draft, "rejected")} disabled={saving} data-testid="idea-reject">
                    Archivia
                  </AdminButton>
                  <AdminButton variant="ghost" onClick={() => setEditing(null)}>Annulla</AdminButton>
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/75">{idea.body}</p>
                {idea.editor_note && <p className="border-l-2 border-sage/50 pl-3 text-sm italic text-foreground/60">{idea.editor_note}</p>}
                <div className="flex flex-wrap gap-2">
                  <AdminButton onClick={() => setEditing({ ...idea })} data-testid="idea-edit">Leggi e modera</AdminButton>
                  {idea.status === "approved" && (
                    <AdminButton variant="ghost" onClick={() => save(idea, "pending")} data-testid="idea-unpublish">
                      <Undo2 size={14} className="inline" /> Togli dal sito
                    </AdminButton>
                  )}
                  <AdminButton variant="ghost" onClick={() => remove(idea.id)} data-testid="idea-delete">
                    <Trash2 size={14} className="inline" /> Elimina
                  </AdminButton>
                </div>
              </div>
            )}
          </AdminCard>
        );
      })}
    </div>
  );
}
